import { fromEvent, Observable, Subject } from 'rxjs';
import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { KeyPairsValue, ProfileDetailModel } from 'src/app/modules/admin/profile-management/profile-detail.model';
import { ProducerService } from '../../../producer.service';
import { ProducerProfile } from '../../../producer-model';
import { ReturnResult } from 'src/app/shared/models/return-result';
import { debounceTime, distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { FlexAlignStyleBuilder } from '@angular/flex-layout';
import { ProfileService } from 'src/app/modules/admin/profile-management/profile.service';
import { Helper } from 'src/app/shared/utility/Helper';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';
import { AddSaleAccountComponent } from 'src/app/modules/admin/sale-account-management/add-sale-account/add-sale-account.component';
import { TblActionType } from 'src/app/shared/enums/tbl-action-type.enum';

/** Identifies a confirmed transition from Add Existing Client to Edit Client. */
export const EDIT_EXISTING_PROFILE_ACTION = 'EDIT_EXISTING_PROFILE';

/** Identifies the persistent autocomplete action that creates a new Client. */
export const CREATE_NEW_CLIENT_ACTION = 'CREATE_NEW_CLIENT';

/** Carries the existing participant selected for a confirmed Edit transition. */
export interface EditExistingProfileSelection {
  action: typeof EDIT_EXISTING_PROFILE_ACTION;
  profileVendor: any;
}

@Component({
  selector: 'app-add-existing-profile',
  templateUrl: './add-existing-profile.component.html',
  styleUrls: ['./add-existing-profile.component.scss'],
  providers: [{
    provide: MAT_SELECT_CONFIG,
    useValue: {
      disableOptionCentering: true,
      overlayPanelClass: 'proposal-role-select-overlay'
    }
  }]
})
export class AddExistingProfileComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('myInput') input: ElementRef;
  @ViewChild(MatAutocompleteTrigger, { read: MatAutocompleteTrigger }) inputAutoComplete: MatAutocompleteTrigger;
  @Input() id: string = '';
  @Input() isProfile = false;
  @Input() tooltipProp: KeyPairsValue[] = [];
  @Input() label: string = "Profile";
  @Input() placeholder: string = null;
  @Input() apiSearch: (data) => Observable<ReturnResult<ProfileDetailModel[]>> = null;
  @Input() isListenEvent: boolean = false;
  // Flag to display first suggest mode required
  @Input() firstSuggest: boolean = true;
  // Search API Output expected: Observable<ReturnResult<any[]>> or Observable<any[]>
  @Input() firstSuggestAPI: Observable<any>;
  // display on Search mat-option
  @Input() displayProp: string;
  //  search Array: Leave empty if passing API and otherwise
  @Input() searchArray: any[] = [];
  @Input() idProp: string = "profileId";
  @Input() showVendorRoles = false;
  @Input() existingProfileVendorsById: Record<string, any> = {};
  vendorRoleOptions = [
    { value: 'BOOKING_CONTRACT', label: 'Booking Contract' },
    { value: 'END_CLIENT', label: 'End Client' },
  ];
  defaultVendorRole = 'BOOKING_CONTRACT';
  loading = false;
  profileList: ProfileDetailModel[] = [];

  selectedProfile: ProfileDetailModel;
  saveLoading = false;
  private signerReplacementConfirmed = false;

  // Lazy scroll loading state (used when isProfile = true)
  private _lazyPageSize = 20;
  private _lazySkip = 0;
  private _lazyTotalCount = 0;
  private _lazyCurrentKeyword = '';
  private searchInputText = '';
  lazyHasMore = false;
  lazyLoading = false;
  private _scrollListenerAttached = false;

  profileFormGroup = this.frmBuilder.group({
    profileFormInput: '',
    roles: [['BOOKING_CONTRACT']],
    isPrimary: false
  });
  findItemByKey = Helper.findItemByKey;
  private _destroy: Subject<void> = new Subject<void>();

  constructor(private producerService: ProducerService,
    private profileService: ProfileService,
    private dialog: MatDialog,
    public dialModalRef: MatDialogRef<AddExistingProfileComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private frmBuilder: FormBuilder) {
    this.id = data.id;
    this.isProfile = data.isProfile;
    this.tooltipProp = data.tooltipProp ?? this.tooltipProp;
    this.placeholder = data.placeholder ?? this.placeholder;
    this.label = data.label ?? this.label;
    this.apiSearch = data.apiSearch ?? this.apiSearch;
    this.isListenEvent = data.isListenEvent ?? this.isListenEvent;
    this.firstSuggest = data.firstSuggest ?? this.firstSuggest;
    this.firstSuggestAPI = data.firstSuggestAPI ?? this.firstSuggestAPI;
    this.displayProp = data.displayProp ?? this.displayProp;
    this.searchArray = data.searchArray ?? this.searchArray;
    this.idProp = data.idProp ?? this.idProp;
    this.showVendorRoles = data.showVendorRoles ?? this.showVendorRoles;
    this.existingProfileVendorsById = data.existingProfileVendorsById ?? this.existingProfileVendorsById;
    this.defaultVendorRole = data.defaultVendorRole ?? this.defaultVendorRole;
    this.profileFormGroup.controls.roles.setValue(
      this.defaultVendorRole === 'NO_ROLE' ? [] : [this.defaultVendorRole]
    );
  }
  ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
  }

  ngOnInit(): void {
    // this.dialModalRef.updateSize('40%', '60%');
    if (this.firstSuggest) {
      this.loading = true;
      this.firstSuggestAPI.pipe(takeUntil(this._destroy), map(x => {
        if (x.result) {
          return x.result.filter(x => x[this.idProp] != this.id);
        }
        return x.filter(x => x[this.idProp] != this.id);
      })).subscribe((res: any[]) => {
        if (res) {
          this.searchArray = res;
          this.loading = false;
          setTimeout(() => {
            this.inputAutoComplete.closePanel();
            this.inputAutoComplete.openPanel();
          }, 100);
        }
      });
    } else if (this.searchArray.length > 0) {
      // already passing an array of results:

    }
  }
  searchProfile(data: string) {
    if (this.firstSuggest) {
      if (data.length >= 0 && data.length < 3) {
        this.profileList = [];
        return;
      }
    }

    if (data === "") {
      const emptyProfile = new ProfileDetailModel();
      emptyProfile.displayName = "Not found";
      this.profileList = [emptyProfile];
      return;
    }

    // Lazy scroll loading for profile search
    if (this.isProfile && this.label.toLowerCase() === "profile") {
      this._lazyCurrentKeyword = data;
      this._lazySkip = 0;
      this.profileList = [];
      this.lazyHasMore = false;
      this._searchProfilePaging(data, 0);
      return;
    }

    //dynamic api search with shadow profile.
    if (this.apiSearch) {
      this.loading = true;
      this.apiSearch(data).subscribe({
        next: res => {
          if (res.result != null) {
            if (res.result.length !== 0)
              this.profileList = res.result;
            else {
              const emptyProfile = new ProfileDetailModel();
              emptyProfile.displayName = "Not found";
              this.profileList = [emptyProfile];
            }
          }
        },
        complete: () => this.loading = false,
      });
      return;
    }

    this.producerService.searchProfile(data);
    this.producerService.getProfileList().subscribe((res) => {
      if (res.result != null) {
        if (res.result.length !== 0)
          this.profileList = res.result;
        else {
          const emptyProfile = new ProfileDetailModel();
          emptyProfile.displayName = "Not found";
          this.profileList = [emptyProfile];
        }
      }
    });
  }

  private _searchProfilePaging(keyword: string, skip: number) {
    this.loading = true;
    this.lazyLoading = skip > 0;
    this.profileService.searchProfilePaging(keyword, skip, this._lazyPageSize).subscribe({
      next: res => {
        if (res.result != null) {
          this._lazyTotalCount = res.result.totalCount;
          if (res.result.data?.length > 0) {
            if (skip === 0) {
              this.profileList = res.result.data;
            } else {
              this.profileList = [...this.profileList, ...res.result.data];
            }
            this._lazySkip = skip + res.result.data.length;
            this.lazyHasMore = this._lazySkip < this._lazyTotalCount;
          } else if (skip === 0) {
            const emptyProfile = new ProfileDetailModel();
            emptyProfile.displayName = "Not found";
            this.profileList = [emptyProfile];
            this.lazyHasMore = false;
          }
        }
      },
      complete: () => {
        this.loading = false;
        this.lazyLoading = false;
      },
    });
  }

  loadMoreProfiles() {
    if (this.lazyLoading || !this.lazyHasMore || !this._lazyCurrentKeyword) return;
    this._searchProfilePaging(this._lazyCurrentKeyword, this._lazySkip);
  }

  onAutocompletePanelOpened() {
    if (!this.isProfile || this._scrollListenerAttached) return;

    // Wait for the panel to render
    setTimeout(() => {
      const panel = document.querySelector('.mat-autocomplete-panel');
      if (panel) {
        this._scrollListenerAttached = true;
        panel.addEventListener('scroll', this._onPanelScroll);
      }
    }, 200);
  }

  onAutocompletePanelClosed() {
    if (!this.isProfile) return;
    const panel = document.querySelector('.mat-autocomplete-panel');
    if (panel) {
      panel.removeEventListener('scroll', this._onPanelScroll);
    }
    this._scrollListenerAttached = false;
  }

  private _onPanelScroll = (event: Event) => {
    const element = event.target as HTMLElement;
    // Trigger when scrolled to ~80% of the panel
    const threshold = element.scrollHeight - element.clientHeight * 1.2;
    if (element.scrollTop >= threshold) {
      this.loadMoreProfiles();
    }
  };

  ngAfterViewInit() {
    fromEvent(this.input.nativeElement, "keyup").pipe(
      //get value in the input
      map((event: any) => {
        return event.target.value;
      }),
      // if character length greater then 2
      filter(res => res.length > 2 || res.length === 0),
      debounceTime(1000),
      distinctUntilChanged(),
    ).subscribe((keyword: string) => {
      //subscribe to keyword to get API
      this.searchProfile(keyword);
    });
  }
  clearSaleAccount() {
    this.searchInputText = '';
    this.profileFormGroup.controls.profileFormInput.setValue("");
    this.selectedProfile = undefined;
    this.resetProposalParticipation();
  }
  displayProfile(value: string | ProfileDetailModel): string {
    return typeof value === 'string' ? value : value?.displayName ?? '';
  }

  /**
   * Remembers the user's literal search text before autocomplete actions update the form control.
   *
   * @param value - Current text typed into the Client search input.
   */
  rememberSearchInput(value: string): void {
    this.searchInputText = value ?? '';
  }

  /**
   * Identifies the non-selectable placeholder shown when a search has no matches.
   *
   * @param profile - Autocomplete row being rendered or selected.
   * @returns `true` when the row is the generated Not found placeholder.
   */
  isNotFoundProfile(profile: ProfileDetailModel): boolean {
    return !profile?.profileId && profile?.displayName === 'Not found';
  }

  /**
   * Selects a profile and keeps this dialog open while a duplicate transition is confirmed.
   *
   * @param data - Selected profile object or legacy display-name value.
   */
  async showSelectedProfile(data: string | ProfileDetailModel): Promise<void> {
    if (data === CREATE_NEW_CLIENT_ACTION) {
      this.profileFormGroup.controls.profileFormInput.setValue(this.searchInputText, { emitEvent: false });
      this.inputAutoComplete.closePanel();
      this.createClient();
      return;
    }

    if (typeof data !== 'string' && this.isNotFoundProfile(data)) {
      return;
    }

    this.selectedProfile = undefined;
    this.resetProposalParticipation();

    if (typeof data !== 'string') {
      this.selectedProfile = data;
    }

    if (this.profileList && this.profileList.length > 0 && !this.selectedProfile)
      this.selectedProfile = this.profileList.find(x => x.displayName == data);

    // if first suggest being selected: 
    if (this.firstSuggest && this.searchArray && this.searchArray?.length > 0 && !this.selectedProfile) {
      this.selectedProfile = this.searchArray.find(x => x.displayName == data);
    }

    const selectedProfileId = this.selectedProfile?.profileId;
    const existingProfileVendor = selectedProfileId
      ? this.existingProfileVendorsById[selectedProfileId]
      : null;
    if (existingProfileVendor) {
      const editExistingClient = await this.openNestedConfirmation(
        'This client is already part of this proposal. Would you like to edit its roles and Contract Signer status?',
        'Edit client',
        'Keep searching'
      );
      if (!editExistingClient) {
        this.clearSaleAccount();
        return;
      }

      const result: EditExistingProfileSelection = {
        action: EDIT_EXISTING_PROFILE_ACTION,
        profileVendor: existingProfileVendor
      };
      this.dialModalRef.close(result);
    }
  }

  /**
   * Opens Client creation above Add Existing Client and selects the newly created Profile.
   *
   * @remarks The Proposal participant is not persisted here; the operator still reviews roles
   * and uses the existing Add action after returning to this dialog.
   */
  createClient(): void {
    const dialogRef = this.dialog.open(AddSaleAccountComponent, {
      disableClose: true,
      height: '100vh',
      width: '600px',
      panelClass: 'dialog-detail',
      autoFocus: false,
      data: {
        action: TblActionType.Add
      }
    });

    dialogRef.afterClosed().subscribe((profileId: string) => {
      if (!profileId) {
        return;
      }

      this.loading = true;
      this.profileService.getProfileByIdLite(profileId).subscribe({
        next: response => {
          if (response.result) {
            this.inputAutoComplete.closePanel();
            this.resetProposalParticipation();
            this.selectedProfile = response.result;
            this.profileFormGroup.controls.profileFormInput.setValue(response.result);
          }
        },
        complete: () => this.loading = false
      });
    });
  }

  /**
   * Confirms replacing the current signer as soon as the operator enables the switch.
   *
   * @param checked - Requested switch state after the user interaction.
   */
  async onSignerChanged(checked: boolean): Promise<void> {
    this.signerReplacementConfirmed = false;
    if (!checked || !this.hasExistingSigner()) {
      return;
    }

    this.profileFormGroup.controls.isPrimary.setValue(false, { emitEvent: false });
    const replaceSigner = await this.openNestedConfirmation(
      'This proposal already has a Contract Signer. Replace them with the selected client?',
      'Replace signer',
      'Keep current signer'
    );
    this.signerReplacementConfirmed = replaceSigner;
    this.profileFormGroup.controls.isPrimary.setValue(replaceSigner, { emitEvent: false });
  }

  /**
   * Opens a confirmation above Add Existing Client without closing the parent dialog.
   *
   * @param message - Confirmation message shown to the operator.
   * @param yesTitle - Label for the confirming action.
   * @param falseTitle - Label for the cancelling action.
   * @returns Whether the operator confirmed the transition.
   */
  private async openNestedConfirmation(
    message: string,
    yesTitle: string,
    falseTitle: string
  ): Promise<boolean> {
    const result = await this.dialog.open(ConfirmModalComponent, {
      panelClass: 'custom-z-index-confirm',
      disableClose: true,
      data: {
        message,
        yesTitle,
        falseTitle,
        zIndex: 1005
      }
    }).afterClosed().toPromise();

    return result === true;
  }

  /**
   * Determines whether the proposal already contains an active contract signer.
   *
   * @returns `true` when any preloaded participant is marked primary.
   */
  private hasExistingSigner(): boolean {
    return Object.keys(this.existingProfileVendorsById)
      .some(profileId => this.existingProfileVendorsById[profileId]?.isPrimary === true);
  }

  /**
   * Restores proposal controls whenever the selected client changes or is cleared.
   */
  private resetProposalParticipation(): void {
    if (!this.showVendorRoles) {
      return;
    }

    this.profileFormGroup.controls.roles.setValue(
      this.defaultVendorRole === 'NO_ROLE' ? [] : [this.defaultVendorRole]
    );
    this.profileFormGroup.controls.isPrimary.setValue(false, { emitEvent: false });
    this.signerReplacementConfirmed = false;
  }

  closeDialog(isRefreshing: boolean) {
    this.dialModalRef.close(isRefreshing);
  }

  saveProfile() {
    //output event to parent component
    if (this.isListenEvent) {
      if (!this.selectedProfile) return;
      if (this.showVendorRoles) {
        (this.selectedProfile as any).roles = this.getPersistedVendorRoles();
        (this.selectedProfile as any).isPrimary = this.profileFormGroup.controls.isPrimary.value;
        (this.selectedProfile as any).replaceExistingSigner = this.signerReplacementConfirmed;
      }
      this.dialModalRef.close(this.selectedProfile)
      return;
    }

    //save relationship function.
    if (this.selectedProfile) {
      this.saveLoading = true;
      if (this.isProfile) {
        this.profileService.addProfileRelationship(this.id, this.selectedProfile?.profileId).subscribe({
          next: (res: ReturnResult<boolean>) => {
            if (res.result === true)
              this.closeDialog(res.result);
          },
          error: () => this.saveLoading = false,
          complete: () => this.saveLoading = false,
        });
      }
      else {
        let profileProducer: ProducerProfile = {
          producerId: this.id,
          profileId: this.selectedProfile.profileId
        }
        this.producerService.addProducerToProfile(profileProducer).subscribe({
          next: (res: ReturnResult<boolean>) => {
            if (res.result === true)
              this.closeDialog(res.result);
          },
          error: () => this.saveLoading = false,
          complete: () => this.saveLoading = false,
        });
      }

    }
  }

  /**
   * Returns visible vendor roles selected in the form control.
   * @returns Visible roles selected by the operator.
   */
  private getPersistedVendorRoles(): string[] {
    return (this.profileFormGroup.controls.roles.value as string[]) ?? [];
  }
}
